define('core/services/I18nService',['angular', 'core/services/TimeService'], function (angular) {
    'use strict';
    return angular.module('dnaApp').service('I18nService', ['$rootScope', '$log', '$http', '$q', 'TimeService',
        function ($rootScope, $log, $http, $q, TimeService) {
            /* The global repository for all the properties. Each time a prop file is read from the server it is added to
             the map. */
            var map = {};

            // Map to use when we switch languages to one that doesn't support all the text on a page.
            var defaultMap;

            /* Currently outstanding requests for a given url/file. */
            var fetching = {};
            /* When an attempt to download a file fails, we put the time of failure here so that we don't make another
             request until we've waited a period of time. */
            var downloadErrors = {};
            /* Once a file is downloaded, we put its name here so that we don't get the same file again. */
            var downloadedFiles = {};
            /* We wait for 1 minute before attempting to download a file that has failed. */
            var ERROR_WAIT_TIME = 60 * 1000;

            var ACTIVATION_LOCALES = ['en','es','fr','de','it','pt','sv'];
            var COMPLETE_LOCALES = dna.app.languages ? dna.app.languages.split(',').map(function(lang) {
                return lang.trim();
            }) : ['en', 'fr', 'es'];
            var CORE_LOCALE = 'en';
            var SPECIFIC_LOCALES = {
                'en-ca': ['kitStatus'], 'en-au': ['kitStatus'], 'en-gb': ['kitStatus']
            };


            /* pre populate the map with skeleton strings so there is no flash */
            if (dna.app.siteLocale && window.dna_load_strings) {
                var preloadLocale = getLocale(dna.app.siteLocale);
                if (window.dna_load_strings[preloadLocale]) {
                    map = window.dna_load_strings[preloadLocale];
                } else {
                    map = window.dna_load_strings.en;
                }
            }

            /*
             * NOTE: Currently (2/12/15) the most arguments for a single string is 8, therefore we create 10 regular
             * expressions. If we ever exceed 10 arguments in a string, the parameterize function will throw an index out
             * of bounds exception. If that ever happens, just increase the number of regular expressions in this array.
             */
            var regExs = [/\{0\}/g, /\{1\}/g, /\{2\}/g, /\{3\}/g, /\{4\}/g, /\{5\}/g, /\{6\}/g, /\{7\}/g, /\{8\}/g, /\{9\}/g];

            /**
             * @method keyNotFound
             * @param key The key that was not found. The error string returned from this method is so that we can
             * immediately see that we are requesting a key that does not exist in the properties file.
             * @returns {String} The '*** ' + key + ' ***' string if we are in "debug" mode, otherwise a blank is returned.
             * @private
             */
            function keyNotFound(key) {
                return dna.app.debugEnabled ? '*** ' + key + ' ***' : undefined;
            }

            /**
             * @method parameterize
             * @desc Substitute any parameters {n} with the values in the args parameter.
             * @param key The property key.
             * @param args Used for argument substitution.
             * @returns {String} The property string with arguments substituted.
             * @private
             */
            function parameterize(key, args) {
                var i;
                var result = map[key];

                if (!result) {
                    return keyNotFound(key);
                }
                if (args && args.length) {
                    for (i = 0; i < args.length; i++) {
                        // Don't ever show 'undefined' or 'null' as the value. However, zero is a valid argument value.
                        var arg = args[i] || args[i] === 0 ? args[i] : '';
                        result = result.replace(regExs[i], arg);
                    }
                }
                return result;
            }

            /**
             * @method resolve
             * @desc Resolve any requests that were made for keys of a particular properties file (i.e. url). Each request is
             * saved in the "fetching" map so that when the properties file comes back from the server we can fulfill
             * all of the requests that were made for the same file.
             * @param url The url of the requested properties file. This is the key into the "fetching" map.
             * @private
             */
            function resolve(url) {
                var promises = fetching[url];
                for (var i = 0; i < promises.length; i++) {
                    var key = promises[i].key;
                    var deferred = promises[i].deferred;
                    var args = promises[i].args;
                    var value;
                    if (args) {
                        value = parameterize(key, args);
                    } else {
                        value = map[key] ? map[key] : keyNotFound(key);
                    }
                    deferred.resolve(value);
                }
                fetching[url] = null;
            }

            /**
             * @method reject
             * @desc Reject any requests that were made for keys of a particular properties file (i.e. url). Each request is
             * saved in the "fetching" map so that when an error occurs we can notify all the requesters that an error
             * occurred.
             * @param url The url of the requested properties file. This is the key into the "fetching" map.
             * @param errorReason The reason the request failed.
             * @private
             */
            function reject(url, errorReason) {
                var promises = fetching[url];
                for (var i = 0; i < promises.length; i++) {
                    var deferred = promises[i].deferred;
                    deferred.reject(errorReason);
                }
                fetching[url] = null;
            }

            /**
             * @method errorInEffect
             * @desc Determine if we are currently in the error waiting period. This is to prevent multiple calls to the
             * server for a file that recently resulted in an error. If we fail to download a file, we wait for
             * ERROR_WAIT_TIME milliseconds until we make a new attempt.
             * @param fileName The name of file that we are trying to download.
             * @returns {*|boolean}
             */
            function errorInEffect(fileName) {
                var errorTime = downloadErrors[fileName];
                return errorTime > 0 && TimeService.withinTimePeriod(errorTime, ERROR_WAIT_TIME);
            }

            function getLocale(locale, fileName, fileArea) {
                var loc = locale.substring(0,2).toLowerCase();
                if (COMPLETE_LOCALES.indexOf(loc) !== -1) { //is it one of our complete translations
                    return loc;
                }
                if (fileName === 'activation' && fileArea === 'instructions' && ACTIVATION_LOCALES.indexOf(loc) !== -1) {
                    return loc;
                }
                return CORE_LOCALE;
            }

            /**
             * @method loadProperties
             * @desc Asynchronously loads the properties file that contains the given key if it hasn't already been
             * loaded. The name of the file to load is derived from the first token in the "key" parameter.
             * @param key The key of the requested value
             * @param args Arguments to insert into the value from the properties file.
             * @private
             */
            function loadProperties(key, args, locale, fullSpecificLocale) {
                var deferred = $q.defer();
                if (key && !map[key]) {
                    var tokens = key.split('.');
                    var loadingLocale = '';
                    if (!fullSpecificLocale) {
                        loadingLocale = getLocale(locale, tokens[0], tokens[1]);
                    } else {
                        loadingLocale = locale;
                    }
                    var fileName = tokens[0],
                        fileNameKey = fileName + '_' + loadingLocale;
                    // there are some testInfo strings in testSettings properties file. If the filename is 'testInfo' we need to load 'testSettings'
                    if (fileName === 'testInfo') {
                        fileName = 'testSettings';
                    }
                    // If (keyNotInMap && (fileAlreadyDownloaded || recentErrorDownloadingFile)) then return keyNotFound.
                    if (downloadedFiles[fileNameKey] || errorInEffect(fileNameKey)) {
                        deferred.resolve(keyNotFound(key));
                        return deferred.promise;
                    }
                    // Make sure the key is valid before we make a request to the server.
                    if (key[key.length - 1] !== '.' && tokens.length > 1 && tokens[tokens.length - 1] !== 'null') {
                        var url = dna.app.cdnUrl + '/' + dna.app.assetsVersion + '/assets/i18n/' + loadingLocale + '/' + fileName + '.json';
                        if (!fetching[url]) {
                            fetching[url] = [{ key: key, deferred: deferred, args: args }];
                            $http.get(url).success(function (props) {
                                downloadErrors[fileNameKey] = undefined;
                                downloadedFiles[fileNameKey] = true;
                                $.extend(map, props);
                                resolve(url);
                            }).error(function (errorReason) {
                                if (defaultMap && errorReason.status === 404) {
                                    $log.warn('Properties file: ' + fileName + ' not found for language ' + loadingLocale, ':  The key that triggered this error: ' + key, args);
                                    angular.forEach(defaultMap, function(value, key) {
                                        if (key.indexOf(fileName + ".") === 0) {
                                            this[key] = value;
                                        }
                                    }, map);
                                } else {
                                    $log.error('Properties file: ' + fileName + ' not found!', 'The key that triggered this error: ' + key, args);
                                    downloadErrors[fileNameKey] = TimeService.currentTime();
                                    reject(url, errorReason);
                                }
                            });
                        } else {
                            fetching[url].push({ key: key, deferred: deferred, args: args });
                        }
                    }
                } else {
                    deferred.resolve(parameterize(key, args));
                }
                return deferred.promise;
            }


            /**
             * @method callUsingSpecificLocale
             * @desc checks whether to look for translations in specific locales or not
             * and returns a boolean
             * @param key The key of the requested value
             */

            function callUsingSpecificLocale(key) {
                var loadingFilesInSpecificLocale = false;
                if (key && Object.keys(SPECIFIC_LOCALES).indexOf(dna.app.siteLocale) !== -1) {
                    if (SPECIFIC_LOCALES[dna.app.siteLocale].indexOf(key.split('.')[0]) !== -1) {
                        loadingFilesInSpecificLocale = true;
                    }
                }
                return loadingFilesInSpecificLocale;
            }

            /**
             * @class I18nService
             */
            var I18nService = {
                // language is just the language
                // e.g. "en", "us"
                language: dna.app.siteLanguage,
                // locale is the language and county, if there is a country. If there isn't a country, locale is the language.
                // e.g. "en-US", "es-MX"
                locale: dna.app.siteLocale,

                changeLanguage: function (newLang) {
                    this.language = newLang;
                    this.locale = newLang;

                    if (!defaultMap) {
                        defaultMap = angular.extend({}, map);
                    }
                    /* The global repository for all the properties. Each time a prop file is read from the server it is added to
                     the map. */
                    map = {};
                    /* Currently outstanding requests for a given url/file. */
                    fetching = {};
                    /* When an attempt to download a file fails, we put the time of failure here so that we don't make another
                     request until we've waited a period of time. */
                    downloadErrors = {};
                    /* Once a file is downloaded, we put its name here so that we don't get the same file again. */
                    downloadedFiles = {};
                },
                /**
                 * @method get
                 * @desc Returns the value for the specified key. This method returns a value immediately. If the properties
                 * file has not yet been loaded, a blank string is returned. This method is primarily used by i18n filter
                 * because it has not concept of a promise, therefore we need to return something right away. CAUTION: don't
                 * call this method from within JavaScript unless you know what you are doing. Generally (almost always) you
                 * will call getViaPromise from JS code.
                 * @param key The key in a properties file.
                 * @param args Values to be substituted into the value from the properties file.
                 * @returns {String} The value of key.
                 * @public
                 */
                get: function (key, args) {
                    loadProperties(key, args, this.locale, false);
                    if (callUsingSpecificLocale(key)) {
                        loadProperties(key, args, this.locale, true);
                    }
                    return parameterize(key, args);
                },

                /**
                 * @method getViaPromise
                 * @desc Returns the value for the specified key by means of a promise. We need this method so that JS code
                 * can request a property value that is being fetched asynchronously from the server.
                 * @param key The key in a properties file.
                 * @param args Values to be substituted into the value from the properties file.
                 * @returns That will be resolved when the value is available.
                 * @public
                 */
                getViaPromise: function (key, args) {
                    var promise = loadProperties(key, args, this.locale, false);
                    if (callUsingSpecificLocale(key)) {
                        loadProperties(key, args, this.locale, true);
                    }
                    return promise;
                },

                getAcceptedLanguages: function (langCodes) {
                    var deferred = $q.defer();
                    var url = '/public/i18n/language-names?languageCodes=' + langCodes;

                    $http.get(url).success(function (data) {
                        deferred.resolve(data);
                    }).error(function (data, status, headers, config) {
                        deferred.reject(data, status, headers, config);
                    });

                    return deferred.promise;
                }
            };

            // make these i18n functions available to our templates
            $rootScope.i18n = I18nService;
            return I18nService;
        }]);
});
